import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import Scene = Phaser.Scene;
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import PlayerStore from "@/store/player";
import {Creature, WarriorClass} from "@/store/level/types";
import {SellCreaturesNavPanel} from "@/scenes.ui/sell-creatures-nav-panel";
import {CreaturePrice, HeroExchangeDetails, ShopCreature} from "@/store/shop/types";
import {SquareButton} from "@/scenes.ui/square-button";
import {Keys} from "@/constants/key";
import {SpaceTr, Tr, TrSpace} from "@/i18n/i18n";
import ShopStore from "@/store/shop";
import Text = Phaser.GameObjects.Text;
import Line = Phaser.GameObjects.Line;
import BaseSound = Phaser.Sound.BaseSound;
import SoundUtils from "@/utils/sound-utils";
import SkillUtils from "@/utils/skill-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {CreaturesGridView} from "@/scenes.ui/creatures-grid-view";
import {CreaturesExchangeGridView} from "@/scenes.ui/creatures-exchange-grid-view";
import OnceCaller from "@/utils/once-caller";

export class SellCreaturePanel extends VerticalStandardPanel {

    private onAcceptCallback: ((checkedIds: number[], price: number) => unknown) | undefined;

    private oldPriceText: Text | undefined;
    private buyPriceText: Text | undefined;
    private underline: Line | undefined;

    private attackAdditionText: Text | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, sc: ShopCreature, prices: CreaturePrice[],
                buySound: BaseSound | undefined,
                onAcceptCallback: (checkedIds: number[], price: number) => unknown) {
        super(scene, canvas, sc.titles[0]);
        this.setDepth(6000);

        this.onAcceptCallback = onAcceptCallback;

        let nameText = TextUtils.addTextByFamily(scene, Tr('sel.heroes'), 0, -170,
            '#ffd465', 17, 'bold', 400, Fonts.MYRIADPRO_REGULAR);

        let descText = TextUtils.addTextByFamily(scene, Tr('hero.dec.price'), 0, -110,
            '#7c7c7c', 12, 'normal', 360, Fonts.BENGALY_NORMAL);

        let countText = TextUtils.addTextByFamily(scene, TrSpace('her.sel') + '0', 0, 107,
            '#7c7c7c', 12, 'normal', 360, Fonts.BENGALY_NORMAL);

        this.add([nameText, descText, countText]);

        let creatures: Creature[] = PlayerStore.getPlayer().creatures.filter(c => !c.imageId.startsWith('man-') && !c.imageId.startsWith('woman-'));

        let self = this;
        SellCreaturesNavPanel.setupPrices(prices);
        let creaturesNavPanel = new SellCreaturesNavPanel(scene, canvas, 0, -30, creatures,(sumDecrease: number, checkedIds: Set<number>) => {
            self.initPrices(scene, sc.price, sumDecrease);
            countText.setText(TrSpace('her.sel') + String(checkedIds.size));
            if (sumDecrease <= sc.price) {
                self.setAttackAddition(scene, 0);
            } else {
                ShopStore.fetchHeroExchangeDetails(sc.id, Array.from(checkedIds.values())).then((ed: HeroExchangeDetails) => {
                    self.setAttackAddition(scene, ed.additionalAttack);
                });
            }
        });

        this.add(creaturesNavPanel);

        let showGridButton = creatures.length > 4;
        if(showGridButton) {
            descText.x -= 20;
            let creaturesGridButton = SpriteUtils.addSprite(scene, 175, -110, Keys.ATLAS_PROFILE, Keys.UI_PROFILE_SOCIAL);
            creaturesGridButton.setInteractive().on('pointerup', () => {
                OnceCaller.doOnce('clan_creatures_exchange_grid', () => {
                    let historyView = new CreaturesExchangeGridView(scene, canvas, creatures, creaturesNavPanel.getCheckedIds(), (cr: Creature, checked: boolean) => {
                        creaturesNavPanel.checkCallback(cr, checked);
                        creaturesNavPanel.actualizePanels();
                    });
                    historyView.open();
                    this.scene!.add.existing(historyView);
                });
            }, creaturesGridButton);
            creaturesGridButton.setScale(0.5 * CU.S);
            creaturesGridButton.setAlpha(0.4)
            this.add(creaturesGridButton);
        }

        let buyButton = SpriteUtils.scale(new SquareButton(this.scene, 0, 180, 0, Keys.UI_ART_GREEN_BUTTON,
            undefined, Keys.IC_100_MITHRIL, Tr('kupit'), false), 0.8 / CU.S, 0.8 / CU.S);

        buyButton.setCallback(() => {
            SoundUtils.play(buySound);
            onAcceptCallback(this.convert(creaturesNavPanel.getCheckedIds()), Math.max(0, sc.price - creaturesNavPanel.getSumDecrease()));
        });
        this.add(buyButton);

        this.initPrices(scene, sc.price, 0);
    }

    private convert(checkedIds: Set<number>): number[] {
        return Array.from(checkedIds.values());
    }

    public setAttackAddition(scene: Scene, attackAddition: number) {
        if (attackAddition <= 0) {
            if (this.attackAdditionText) {
                this.attackAdditionText.setVisible(false);
            }
            return;
        }
        let txt = '+' + SkillUtils.formatValue(attackAddition) + SpaceTr('add.attack');
        if (this.attackAdditionText) {
            this.attackAdditionText.setVisible(true);
            this.attackAdditionText.setText(txt);
        } else {
            this.attackAdditionText = TextUtils.addTextByFamily(scene, txt, 0, 123,
                '#59ff30', 10, 'bold', 360, Fonts.ARIAL_MT);
            this.add(this.attackAdditionText);
        }
    }

    private initPrices(scene: Scene, price: number, priceDecrease: number) {
        if (this.buyPriceText) {
            this.buyPriceText.setVisible(false);
            this.buyPriceText.destroy();
            this.buyPriceText = undefined;
        }
        if (this.oldPriceText) {
            this.oldPriceText.setVisible(false);
            this.oldPriceText.destroy();
            this.oldPriceText = undefined;
        }
        if (this.underline) {
            this.underline.setVisible(false);
            this.underline.destroy();
            this.underline = undefined;
        }

        const formatedPrice: string = TextUtils.formatGems(price);

        if (priceDecrease <= 0) {
            this.buyPriceText = TextUtils.addText(scene,
                formatedPrice, 0, 208, '#ffffff', 18, 'bold', 200);
        } else {
            let newPrice = Math.max(0, price - priceDecrease);
            this.buyPriceText = TextUtils.addTextAdvanced(scene, TextUtils.formatGems(newPrice), 0, 208,
                '#0cff00', 19, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_BOLD);

            this.buyPriceText.setShadow(0, 0, '#ffffff');
            this.buyPriceText.setStroke('#000000', 2);

            this.oldPriceText = TextUtils.addTextAdvanced(scene, formatedPrice, 0, 185,
                '#ffffff', 13, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_REGULAR);
            this.oldPriceText.setShadow(0, 0, '#ffffff');
            this.oldPriceText.setStroke('#000000', 2);

            this.underline = scene.add.line(0, 0,
                this.oldPriceText.getLeftCenter().x! + 4 * formatedPrice.length * CU.S, this.oldPriceText.getLeftCenter().y!,
                this.oldPriceText.getRightCenter().x! + 14 * CU.S, this.oldPriceText.getRightCenter().y! + 14,0xFF0000, 1);

            this.add([this.oldPriceText, this.underline]);
        }

        this.add([this.buyPriceText]);
    }

    protected onClose(): void {

    }

}
